import React, { ReactNode, useCallback } from 'react';
import cx from 'clsx';
import { useTranslation } from 'react-i18next';

import { Button, ButtonProps } from '@/components';
import { useSwitch } from '@/hooks';
import { wrapModalPromise, WrapModalProps } from '@/utils';

import './index.css';

interface ConfirmModalProps {
  title?: ReactNode | string;
  content?: ReactNode | ((hide: () => void) => ReactNode);
  showCancelBtn?: boolean;
  cancelBtnText?: string;
  confirmBtnText?: ReactNode;
  onConfirmClick?(): Awaited<any>;
  onCancelClick?(): void;
  modalClassName?: string;
  btnClassName?: string;
  closeable?: boolean;
  cancelBtnGhost?: boolean;
  cancelBtnType?: ButtonProps['type'];
}

export const ConfirmModal = ({
  title,
  content,
  showCancelBtn = true,
  cancelBtnText = '',
  confirmBtnText = '',
  cancelBtnGhost = true,
  cancelBtnType = 'primary',
  modalClassName,
  onConfirmClick,
  onCancelClick,
  btnClassName,
  hide,
  onResolve,
  onReject,
}: ConfirmModalProps & WrapModalProps) => {
  const { t } = useTranslation();
  const { on, turnOn, turnOff } = useSwitch();

  const handleCancelClick = () => {
    onCancelClick?.();
    hide?.();
  };

  const handleConfirmClick = useCallback(async () => {
    try {
      turnOn();
      await onConfirmClick?.();
      onResolve?.();
      hide?.();
    } finally {
      turnOff();
    }
  }, [onConfirmClick, onResolve, hide, turnOff, turnOn]);

  return (
    <div className={cx('db-modal db-confirmModal', modalClassName)}>
      {title && <div className="db-confirmModal-title">{title}</div>}
      {content && (
        <div className="db-confirmModal-content">
          {typeof content === 'function' ? content(hide) : content}
        </div>
      )}
      <div className="db-confirmModal-btns">
        {showCancelBtn ? (
          <Button
            type={cancelBtnType}
            ghost={cancelBtnGhost}
            onClick={handleCancelClick}
            className={cx('db-confirmModal-btn', btnClassName)}>
            {cancelBtnText || t('L2PayModal.x9d2fu')}
          </Button>
        ) : null}
        <Button
          type="primary"
          onClick={handleConfirmClick}
          className={cx('db-confirmModal-btn', btnClassName)}
          loading={on}>
          {confirmBtnText || t('L2PayModal.179d1nk')}
        </Button>
      </div>
    </div>
  );
};

export const confirm = wrapModalPromise<ConfirmModalProps>(ConfirmModal);
